import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { placeBalloon } from '../lib/balloonPlacement.ts'
import { Button } from './Button.tsx'

/**
 * Points at something on screen and explains it, once.
 *
 * Shown when a pharmacist asks to review what they left unanswered: they are
 * sent to the first pending difference, and the thing they now have to do is
 * two small buttons on one row of a dense screen. Sending somebody to a place
 * without showing them what to do there is how a control goes unnoticed for
 * weeks.
 *
 * **Built rather than installed.** `driver.js`, `react-joyride` and
 * `shepherd.js` all do this, and each brings a styling system and a
 * positioning engine we would spend longer overriding than writing. This is
 * one coach mark: a hole in a dim backdrop, a balloon with a tail, two buttons.
 *
 * It reappears every time until somebody says not to. Learning where a control
 * is takes more than one encounter, and a hint that vanishes after being seen
 * once is a hint most people see while thinking about something else.
 */
export interface CoachmarkProps {
  /**
   * What to point at. Queried when the mark opens, so it may not exist yet.
   * A list is tried in order and the first that exists is used: a row, and
   * failing that the card it would have been on.
   */
  readonly selector: string | readonly string[]
  readonly title: string
  readonly children: ReactNode
  /** Closes for now; it will be offered again next time. */
  readonly onDismiss: () => void
  /** Closes for good. Unused when `actions` replaces the buttons. */
  readonly onNeverAgain?: (() => void) | undefined
  /**
   * The buttons, in place of "Entendi / Não mostrar de novo".
   *
   * For a mark that is one stop of several (#541): it needs previous and next,
   * and Tab has to move between them instead of dismissing.
   */
  readonly actions?: ReactNode
  /**
   * Points without taking the screen away.
   *
   * The hint for a first decision blocks on purpose: there is one thing to
   * learn and nothing else to do until it is. A mark that explains something
   * a person then has to check against the receita cannot do that -- the page
   * behind it has to stay scrollable, zoomable and clickable. It is dimmed the
   * same; see `also` for what stays lit.
   */
  readonly passive?: boolean
  /**
   * A second thing left undimmed, without a balloon of its own.
   *
   * For a row whose other half is elsewhere on the screen: the row is what is
   * pointed at, and the receita beside it is the evidence it has to be read
   * against. Dimming that would hide what the balloon is asking about.
   */
  readonly also?: string | undefined
  /** The balloon's width. A note with two readings in it needs more room. */
  readonly width?: number
}

/** Breathing room between the highlight and the thing it highlights. */
const HALO = 6

export function Coachmark({
  selector,
  title,
  children,
  onDismiss,
  onNeverAgain,
  actions,
  passive = false,
  also,
  width = 320,
}: CoachmarkProps) {
  const [alsoBox, setAlsoBox] = useState<DOMRect | undefined>()
  const selectors = typeof selector === 'string' ? [selector] : selector
  // By value: a list built during render is a new array every time.
  const selectorKey = selectors.join('\n')
  const [box, setBox] = useState<DOMRect | undefined>()
  const understood = useRef<HTMLButtonElement>(null)
  const balloon = useRef<HTMLDivElement>(null)
  // Its real height, measured once it has rendered: see `placeBalloon`.
  const [height, setHeight] = useState<number | undefined>()
  useLayoutEffect(() => {
    if (balloon.current !== null) setHeight(balloon.current.offsetHeight)
  }, [box])

  /*
   * The keyboard pass runs through here without a mouse: the mark opens with
   * "Entendi" focused, and Tab or Enter presses it -- there is nothing else
   * on the screen to reach while the backdrop is up. Escape is the same
   * answer.
   */
  // Once measured: until then the balloon is hidden, and cannot take focus.
  useEffect(() => {
    if (height !== undefined) understood.current?.focus()
  }, [height])

  /*
   * Measured from the live element, and re-measured while it is open.
   *
   * The row underneath can move: the image finishes loading, a finding
   * arrives, somebody scrolls. A hole cut once ends up over the wrong row, and
   * a spotlight pointing at the wrong control is worse than none.
   */
  useEffect(() => {
    const find = () => {
      for (const one of selectorKey.split('\n')) {
        const target = document.querySelector(one)
        if (target !== null) return target
      }
      return null
    }
    const measure = () => {
      const target = find()
      setBox(target === null ? undefined : target.getBoundingClientRect())
      const second = also === undefined ? null : document.querySelector(also)
      setAlsoBox(second === null ? undefined : second.getBoundingClientRect())
    }

    /*
     * Brought into view first. The row it points at can be below the fold
     * of a scrolled board, and a hole cut around an off-screen rectangle
     * dims the page around nothing.
     */
    find()?.scrollIntoView({ block: 'center' })
    measure()
    // The step is switched in the same tick that opens this, so the first
    // measurement can land before the new row is laid out.
    const settle = setTimeout(measure, 60)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)

    return () => {
      clearTimeout(settle)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [selectorKey, also])

  // Nothing to point at. Rather than dim the screen around a hole in the wrong
  // place, say nothing at all -- the pharmacist is already where they need to
  // be, and a broken hint is worse than a missing one.
  if (box === undefined) return null

  const BALLOON = width
  const TAIL = 12
  const EDGE = 8

  /*
   * Centred on the target, then pulled inside the window.
   *
   * Generic on purpose: nothing here knows that this particular target sits at
   * the right of a wide board. Any element anywhere gets the same treatment,
   * which is what makes the tail arithmetic below correct rather than tuned.
   */
  const centre = box.left + box.width / 2
  const balloonLeft = Math.max(
    EDGE,
    Math.min(centre - BALLOON / 2, window.innerWidth - BALLOON - EDGE),
  )

  /*
   * And the tail points at the target, not at the balloon's middle.
   *
   * The two coincide only when the balloon was not clamped. Near either edge
   * of the window the balloon slides and the target does not, so a tail fixed
   * to the balloon's centre -- or, as it was, guessed from its right edge --
   * ends up under the wrong part of the screen entirely. Clamped so it stays
   * on the balloon even when the target is past its corner.
   */
  const tailLeft = Math.min(Math.max(TAIL, centre - balloonLeft - TAIL / 2), BALLOON - TAIL * 2)

  const placement =
    height === undefined
      ? undefined
      : placeBalloon(box, height, window.innerHeight, HALO + 12, EDGE)

  return (
    <div
      role="dialog"
      aria-modal={passive ? 'false' : 'true'}
      aria-label={title}
      // Passive: the layer lets every click through, and only the balloon
      // takes them back.
      className={`fixed inset-0 z-[70] ${passive ? 'pointer-events-none' : ''}`}
      onKeyDown={(event) => {
        // With its own buttons Tab moves between them; only Escape leaves.
        if (event.key === 'Escape' || (event.key === 'Tab' && actions === undefined)) {
          event.preventDefault()
          onDismiss()
        }
      }}
    >
      {/*
        The hole.

        One element with an enormous shadow spread, rather than four panels
        around the target: the corners stay square, the dim is even, and there
        is nothing to keep in sync when the box moves.
      */}
      {alsoBox !== undefined && (
        /*
         * Two holes cannot be two shadows: each shadow would dim the other's
         * hole. So with a second one the dim is a single sheet with both
         * rectangles cut out of it, and the element below keeps only its
         * outline.
         */
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'rgba(0, 13, 77, 0.55)',
            clipPath: `path(evenodd, "${sheetWithHoles([haloed(box), alsoBox])}")`,
          }}
        />
      )}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute rounded-[8px]"
        style={{
          left: box.left - HALO,
          top: box.top - HALO,
          width: box.width + HALO * 2,
          height: box.height + HALO * 2,
          ...(alsoBox === undefined ? { boxShadow: '0 0 0 9999px rgba(0, 13, 77, 0.55)' } : {}),
          outline: '2px solid var(--color-stroke-brand-strong)',
        }}
      />

      <div
        ref={balloon}
        className={`bg-background-raised pointer-events-auto absolute flex flex-col gap-3 rounded-[10px] p-4 shadow-lg ${
          // Scrolls only when pinned: overflow would clip the tail otherwise.
          placement?.tail === 'none' ? 'overflow-y-auto' : ''
        }`}
        style={{
          width: BALLOON,
          left: balloonLeft,
          top: placement?.top ?? 0,
          maxHeight: window.innerHeight - EDGE * 2,
          // Measured before it is shown, so it never flashes in the wrong place.
          visibility: placement === undefined ? 'hidden' : undefined,
        }}
      >
        {/*
          The tail, pointing back at what this is about.

          A rotated square rather than a border triangle, so it takes the
          balloon's own background and needs no second colour to keep in step.
        */}
        {placement !== undefined && placement.tail !== 'none' && (
          <span
            aria-hidden="true"
            className="bg-background-raised absolute size-3 rotate-45"
            style={{
              // Measured from the balloon's own left edge to the target's
              // centre. The previous version guessed from the right and landed
              // in the opposite corner whenever the balloon was clamped.
              left: tailLeft,
              ...(placement.tail === 'top' ? { top: -6 } : { bottom: -6 }),
            }}
          />
        )}

        <h2 className="text-tH6 text-text-strong">{title}</h2>
        <div className="text-tP text-text-weak flex flex-col gap-2">{children}</div>

        {actions ?? (
          <div className="flex flex-wrap gap-2">
            <Button
              ref={understood}
              size="small"
              variant="primary"
              tone="brand"
              onClick={onDismiss}
            >
              Entendi
            </Button>
            <Button size="small" variant="tertiary" tone="neutral" onClick={onNeverAgain}>
              Não mostrar de novo
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

/** A rectangle grown by the halo, the way the outlined hole is drawn. */
function haloed(box: DOMRect): { left: number; top: number; width: number; height: number } {
  return {
    left: box.left - HALO,
    top: box.top - HALO,
    width: box.width + HALO * 2,
    height: box.height + HALO * 2,
  }
}

/**
 * The whole window as one path, with each rectangle cut out of it.
 *
 * Even-odd, so a rectangle inside the outer one is a hole rather than more of
 * the sheet. Exported to be tested: a wrong path dims nothing, or everything.
 */
export function sheetWithHoles(
  holes: ReadonlyArray<{ left: number; top: number; width: number; height: number }>,
  viewport: { width: number; height: number } = {
    width: window.innerWidth,
    height: window.innerHeight,
  },
): string {
  const outer = `M0 0H${String(viewport.width)}V${String(viewport.height)}H0Z`
  const cut = holes.map((hole) => {
    const left = Math.round(hole.left)
    const top = Math.round(hole.top)
    const right = Math.round(hole.left + hole.width)
    const bottom = Math.round(hole.top + hole.height)
    return `M${String(left)} ${String(top)}H${String(right)}V${String(bottom)}H${String(left)}Z`
  })
  return [outer, ...cut].join('')
}

/**
 * Whether somebody has asked never to see a particular hint again.
 *
 * Kept in the browser rather than on the account: it is about this person on
 * this machine having seen a thing, which is exactly the scope
 * `localStorage` has. Wrapped both ways because a private window or undecided
 * storage throws, and a hint is never a reason for a screen to fail.
 */
export function coachmarkDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(`coach.${key}`) === 'never'
  } catch {
    return false
  }
}

export function dismissCoachmarkForever(key: string): void {
  try {
    window.localStorage.setItem(`coach.${key}`, 'never')
  } catch {
    // Shown again next time, which is the honest degradation.
  }
}
